import { assign } from 'xstate';
import getDCHMessageType from '../../helpers/getDCHMessageType';
import { MESSAGES, PAGE_FUNCTION, PAGE_NAME, SUB_FUNCTION } from '../../const/analytics';
import { PINLESS_DEBIT_CARD } from '../../const/paymentMedium';
import { paymentOptions, paymentTypes } from '../../const/paymentOptions';
import { getIsBankrupt, isInstallmentLoan, isMonthly, isSecuredInstallmentLoanProduct } from '../../helpers';
import isDCHSettlementEnrolled from '../../helpers/isDCHSettlementEnrolled';
import { getValues } from '../../xform';
import { getAnalyticsMessageType, getPaymentFlowFormValues } from '../helpers';
export const savePaymentResult = assign({
    paymentResult: (context, { data }) => {
        return data;
    }
});
export const getIsValidAutopayPaymentOption = (accountSummary, paymentOption, paymentAmount, paymentMethod) => {
    const productType = accountSummary.product.type;
    const { customerAssistancePlan, totalMinimumPaymentDue } = accountSummary.billing;
    if (!productType) {
        return false;
    }
    const validRCPaymentOptions = [
        paymentOptions.STATEMENT_BALANCE,
        paymentOptions.MINIMUM_PAYMENT_DUE,
        paymentOptions.OTHER_PAYMENT
    ];
    const validRCPaymentOptionCAPEnrolled = paymentOptions.FIXED_PAYMENT;
    if (paymentMethod.paymentMedium === PINLESS_DEBIT_CARD) {
        return false;
    }
    if (isInstallmentLoan(productType)) {
        if (paymentOption === paymentOptions.OTHER_PAYMENT) {
            return Number(paymentAmount) > Number(totalMinimumPaymentDue);
        }
        return paymentOption === paymentOptions.TOTAL_AMOUNT_DUE;
    }
    if (customerAssistancePlan) {
        return paymentOption === validRCPaymentOptionCAPEnrolled;
    }
    return validRCPaymentOptions.includes(paymentOption);
};
export const saveShouldShowOneClickAutopay = assign({
    shouldShowOneClickAutopay: (context) => {
        const { apiMachineRef, form, featureFlags } = context;
        const { accountSummaryQueryResult: { data: accountSummary }, autopayStatusQueryResult: { data: autopayStatus }, paymentEnterpriseEligibilityQueryResult: { data: paymentEnterpriseEligibility } } = apiMachineRef.getSnapshot().context;
        const { paymentAmount, paymentOption } = getValues(form.getSnapshot().context.actors);
        const [, paymentMethod] = getPaymentFlowFormValues(context);
        const autopayEnrollEligibility = paymentEnterpriseEligibility === null || paymentEnterpriseEligibility === void 0 ? void 0 : paymentEnterpriseEligibility.AUTOPAY.find(({ subEligibilityType }) => subEligibilityType === 'AUTOPAY_ENROLL');
        const isValidProduct = !(isMonthly(accountSummary.product.type) ||
            isSecuredInstallmentLoanProduct(accountSummary.product.type));
        const isBankrupt = getIsBankrupt(accountSummary.conditions);
        const isValidAutopayPaymentOption = getIsValidAutopayPaymentOption(accountSummary, paymentOption, paymentAmount, paymentMethod);
        const isSettlement = isDCHSettlementEnrolled(accountSummary.billing, accountSummary.product.type, featureFlags);
        return (!isSettlement &&
            (autopayStatus === null || autopayStatus === void 0 ? void 0 : autopayStatus.enrollment) === false &&
            isValidProduct &&
            autopayEnrollEligibility.eligible &&
            !isBankrupt &&
            isValidAutopayPaymentOption);
    }
});
const paymentResiliencyAnalytics = (context) => {
    const { paymentResult: { hasOneClickAutopayError, shouldHaveEnrolledInOneClickAutopay } } = context;
    const message = 'Error:202 We will send an email when your payment is applied to your account.';
    if (shouldHaveEnrolledInOneClickAutopay) {
        if (!hasOneClickAutopayError) {
            /*
             * One-time Payment was pending
             * Autopay was successful
             */
            return {
                pageFunction: PAGE_FUNCTION.MAKE_PAYMENT.PAYMENTS,
                pageSubFunction: SUB_FUNCTION.MAKE_PAYMENT.ONETIME_PENDING,
                pageName: PAGE_NAME.MAKE_PAYMENT.ONETIME_PENDING,
                message
            };
        }
        /*
         * One-time Payment was pending
         * Autopay was unsuccessful
         */
        return {
            pageFunction: PAGE_FUNCTION.MAKE_PAYMENT.PAYMENTS,
            pageSubFunction: SUB_FUNCTION.MAKE_PAYMENT.ONETIME_PENDING,
            pageName: PAGE_NAME.AUTOPAY.AUTOPAY_FAILURE,
            message
        };
    }
    /*
     * One-time Payment was pending
     * Not checked for Autopay enrollment
     */
    return {
        pageFunction: PAGE_FUNCTION.MAKE_PAYMENT.PAYMENTS,
        pageSubFunction: SUB_FUNCTION.MAKE_PAYMENT.PAYMENT,
        pageName: PAGE_NAME.MAKE_PAYMENT.PENDING,
        message
    };
};
const oneClickAutopayAnalytics = (context) => {
    const { paymentResult } = context;
    const { hasOneClickAutopayError } = paymentResult;
    if (!hasOneClickAutopayError) {
        /*
         * One-time Payment was successful
         * Autopay was successful
         */
        return {
            pageFunction: PAGE_FUNCTION.MAKE_PAYMENT.PAYMENTS,
            pageSubFunction: SUB_FUNCTION.ONE_CLICK_AUTOPAY.ONETIME_AUTOPAY,
            pageName: PAGE_NAME.AUTOPAY.SUCCESS
        };
    }
    /*
     * One-time Payment was successful
     * Autopay was unsuccessful
     */
    return {
        pageFunction: PAGE_FUNCTION.MAKE_PAYMENT.PAYMENTS,
        pageSubFunction: SUB_FUNCTION.MAKE_PAYMENT.ONETIME_SUCCESS,
        pageName: PAGE_NAME.AUTOPAY.AUTOPAY_FAILURE
    };
};
export const submitFailureAnalyticsHandler = (context) => {
    const { paymentResult, apiMachineRef, featureFlags } = context;
    const { shouldHaveEnrolledInOneClickAutopay } = paymentResult;
    const messageTypeDCH = getDCHMessageType(featureFlags.isDigitalCollectionsEnabled, apiMachineRef.getSnapshot().context.accountSummaryQueryResult.data);
    if (shouldHaveEnrolledInOneClickAutopay) {
        return Object.assign({ pageFunction: PAGE_FUNCTION.MAKE_PAYMENT.PAYMENTS, pageSubFunction: SUB_FUNCTION.ONE_CLICK_AUTOPAY.ONETIME_AUTOPAY, pageName: PAGE_NAME.AUTOPAY.FAILED }, (messageTypeDCH && { messageType: messageTypeDCH }));
    }
    return Object.assign({ pageFunction: PAGE_FUNCTION.MAKE_PAYMENT.PAYMENTS, pageSubFunction: SUB_FUNCTION.MAKE_PAYMENT.PAYMENT, pageName: PAGE_NAME.MAKE_PAYMENT.FAILED, message: 'paymentTypeOneTime', errorMessage: MESSAGES.ERROR.MAKE_PAYMENT.PAYMENT_FAILED }, (messageTypeDCH && { messageType: messageTypeDCH }));
};
export const submitSuccessAnalyticsHandler = (context) => {
    const { paymentResult: { isPaymentPending, shouldHaveEnrolledInOneClickAutopay } } = context;
    if (isPaymentPending) {
        return paymentResiliencyAnalytics(context);
    }
    if (shouldHaveEnrolledInOneClickAutopay) {
        return oneClickAutopayAnalytics(context);
    }
    /*
     * One-time Payment was successful
     * Not checked for Autopay enrollment
     */
    const [paymentOption] = getPaymentFlowFormValues(context);
    const messageType = getAnalyticsMessageType(context.apiMachineRef, paymentTypes.ONCE, paymentOption);
    return Object.assign({ pageFunction: PAGE_FUNCTION.MAKE_PAYMENT.PAYMENTS, pageSubFunction: SUB_FUNCTION.MAKE_PAYMENT.PAYMENT, pageName: PAGE_NAME.COMPLETE, message: 'paymentTypeOneTime' }, (messageType && { messageType }));
};
const getPageNameForReview = (paymentMethod) => {
    const { REVIEW_CHECKING, REVIEW_DEBIT, REVIEW_SAVINGS, REVIEW_CREDIT } = PAGE_NAME.MAKE_PAYMENT;
    if (paymentMethod.paymentMedium === 'PINLESS_DEBIT_CARD')
        return REVIEW_DEBIT;
    if (paymentMethod.paymentMedium === 'CREDIT_CARD')
        return REVIEW_CREDIT;
    if (paymentMethod.type === 'CHECKING')
        return REVIEW_CHECKING;
    return REVIEW_SAVINGS;
};
export const submitReviewAnalyticsHandler = (context) => {
    const [paymentOption, paymentMethod] = getPaymentFlowFormValues(context);
    const messageType = getAnalyticsMessageType(context.apiMachineRef, paymentTypes.ONCE, paymentOption);
    return Object.assign({ message: paymentOption, pageFunction: PAGE_FUNCTION.MAKE_PAYMENT.PAYMENTS, pageName: getPageNameForReview(paymentMethod), pageSubFunction: SUB_FUNCTION.MAKE_PAYMENT.ONETIME_REVIEW }, (messageType && { messageType }));
};
